import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setBookSession } from 'app/actions/scheduling/bookSession';
import { DateTime } from 'luxon';
import { RootState } from 'typedefs';
import { useTranslation } from 'react-i18next';

import { CoachProfileModel } from 'app/models/scheduling/CoachProfileModel';
import { cls } from 'app/utils';

import ShowDate from './ShowDate';
import TimeSlots from './TimeSlots';
import { partOfDay } from 'app/routes/Sessions/AllCoachesPublicSession/_components/byDateAvailability';

import styles from './DateTime.module.scss';

// Keyed on the plain words, which is what the rest of the app is translated
// under — `Morning` and `Afternoon` were already there in both languages, while
// the shouted variants these used to carry existed only in English. The frame's
// capitals are a `text-transform` on the label, where casing belongs.
const PART_LABELS = [
  { part: 'morning' as const, label: 'Morning' },
  { part: 'afternoon' as const, label: 'Afternoon' },
  { part: 'evening' as const, label: 'Evening' },
];

type PropsDef = {
  coach: CoachProfileModel;
  day: DateTime;
  current?: boolean;
  timeSlots: any;
  singleDay?: boolean;
  multiCoach?: boolean;
  // The booking option the user has selected (single-coach flow). Absent in the
  // multi-coach browse view, where the coach's default rate is used instead.
  pricingValue?: CoachProfileModel['defaultLessonRate'];
  submitForm: () => void;
};

const DayRow = ({
  coach,
  day,
  current = false,
  timeSlots,
  singleDay = false,
  multiCoach = false,
  pricingValue,
  submitForm,
}: PropsDef) => {
  const dispatch = useDispatch();
  const time = useSelector((state: RootState) => state.bookSession.time);
  const { t } = useTranslation();

  const onSlotSelect = (slot) => {
    dispatch(
      setBookSession({
        date: slot.startHour.startOf('day').toISODate(),
        time: {
          startHour: slot.startHour.toMillis(),
          endHour: slot.endHour.toMillis(),
          timeZone: slot.startHour.get('zone').zoneName,
          coachId: slot.coachProfileId,
        },
        coach,
        // CN-2592: keep the option the user actually selected. Reverting to the
        // coach's default rate here booked (e.g.) a 30-min slot under the 60-min
        // default option — a duration/price mismatch. The multi-coach browse view
        // has no selected option, so it still falls back to the coach default.
        pricingValue: multiCoach
          ? coach.defaultLessonRate
          : pricingValue ?? coach.defaultLessonRate,
        duration: slot.duration,
      })
    );
  };

  return (
    // `cls` rather than a template literal: `current && !singleDay && …` is
    // `false` when either test fails, and a template stringifies that — the row
    // was going out as `class="day_row false"`. `cls` drops a falsy argument.
    <div className={cls(styles.day_row, current && !singleDay && styles.current_day)}>
      {!singleDay && <ShowDate date={day} />}

      {/* CN-2652: the frame groups a day's slots under MORNING / AFTERNOON /
          EVENING. Empty groups are dropped, and a day with nothing at all falls
          through to TimeSlots' own empty state. */}
      {(timeSlots || []).length === 0 ? (
        <TimeSlots
          timeSlots={[]}
          noSlotsText={
            current
              ? t('No available timeslots on the selected day.')
              : t('No available timeslots on this day.')
          }
          selectedSlot={time}
          onSlotSelect={onSlotSelect}
          multiCoach={multiCoach}
          submitForm={submitForm}
        />
      ) : (
        PART_LABELS.map(({ part, label }) => {
          const partSlots = (timeSlots || []).filter(
            (slot) => partOfDay(slot.startHour) === part
          );

          if (partSlots.length === 0) {
            return null;
          }

          return (
            <div className={styles.slot_part} key={part}>
              <div className={styles.slot_part_label}>{t(label)}</div>
              <TimeSlots
                timeSlots={partSlots}
                noSlotsText={''}
                selectedSlot={time}
                onSlotSelect={onSlotSelect}
                multiCoach={multiCoach}
                submitForm={submitForm}
              />
            </div>
          );
        })
      )}

    </div>
  );
};

export default DayRow;
